import { frameworkOptions, type StepProps, stripUnsafeChars } from './wizard-data'
import { FrameworkPill } from './wizard-primitives'

// Rendered inline, nested under the selected SIP/audio build option (see
// StepBuild) — sharing its card border via a top divider, not its own
// separate card, so the two read as one continuous block.
//
// This is main's shipped interaction, deliberately unchanged: radio pills, one
// answer, and "Custom stack"
// is itself an option whose selection reveals a free-text field below the row.
// Only the PLATFORM LISTS differ from main — per build, see frameworkOptions.
export const StepFramework = ({ state, update }: StepProps) => {
  const options = frameworkOptions(state.build)
  const selected = options.find((o) => o.value === state.framework)
  return (
    // The SAME inset grey panel StepAgent uses, replacing
    // main's full-width top-divider section: the two follow-ups are the same
    // kind of thing — a nested question inside the selected card — so they now
    // look it, and the panel marks that boundary better than a rule across the
    // card. bg-bg3 + m-2 + p-3 + rounded-md is the repo's existing nested-question
    // shape (unified-form.config.tsx does it for Answer URL).
    <div className="m-2 flex flex-col gap-3 rounded-md bg-bg3 p-3">
      {/* neo's Label style (text-secondary text-s-500), matching StepAgent's
          question exactly — same panel, same label treatment. */}
      <p className="text-s-500 text-secondary">What will you connect it to?</p>

      {/* gap-1.5 with px-2.5 pills: at gap-2/px-3 the four SIP pills overflow by 6px. */}
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => (
          <FrameworkPill
            key={option.value}
            option={option}
            selected={state.framework === option.value}
            // Clear the free-text detail when switching options.
            onSelect={(v) => update({ framework: v, frameworkDetail: '' })}
          />
        ))}
      </div>

      {/* Free-text field for "Custom stack". A single-line <input>, not the
          2-row textarea main ships: the answer is a stack
          NAME — a phrase, not prose — and a 2-row box made the follow-up the
          tallest thing on the card while inviting paragraphs the question never
          asked for. h-9, matching every other control in the probe (same call
          as StepAgent's field, which answers the same kind of question). */}
      {selected?.detailPlaceholder && (
        <input
          value={state.frameworkDetail}
          // Angle brackets are stripped at the input so the backend's unsafe-char
          // guard can never turn a finished wizard into a 400. Deliberately no
          // length cap — the backend doesn't impose one, and cutting a
          // qualification answer short loses the detail we asked for.
          onChange={(e) => update({ frameworkDetail: stripUnsafeChars(e.target.value) })}
          placeholder={selected.detailPlaceholder}
          // A placeholder is NOT a label — it disappears on the first keystroke
          // and several screen readers ignore it outright, so without this the
          // field announces as an unnamed text input. Derived from the option
          // that revealed it ("Custom stack details") so the name can never
          // drift from the pill above it, and so a future detail-bearing option
          // gets a correct name for free.
          aria-label={`${selected.label} details`}
          data-testid="onboarding-framework-detail"
          // Same field treatment as StepAgent's, since the two panels are now
          // the same block: white on the grey panel, h-9 like every control in
          // it, placeholder one weight down from the typed value.
          className="h-9 w-full rounded-lg border border-st2 bg-background px-3 text-foreground text-s-500 outline-none transition-colors placeholder:text-s-400 placeholder:text-secondary focus-visible:ring-1 focus-visible:ring-ring"
        />
      )}
    </div>
  )
}
